/**
 * Reservation Queue outbox event schema
 *
 * Defines the payload shape for:
 * - `reservation.queue_promoted` — emitted when a queued entry is promoted
 *   into an active reservation (via cancellation, TTL expiry, admin action,
 *   or the worker's scheduled-promotion sweep — see CU-86cb4wky2)
 *
 * The event type constant is append-only and MUST NEVER be renamed.
 *
 * Traceability: REQ-4.2, REQ-3.9 -> reservation-waitlist / Queue contracts and outbox event schema
 */
import { z } from 'zod';
import { QueueEntrySubtypeSchema } from '../reservation-status.js';

/**
 * Append-only event type string for the reservation queue promotion event.
 * Downstream consumers rely on this exact value — renaming it is forbidden.
 */
export const RESERVATION_QUEUE_PROMOTED_EVENT_TYPE = 'reservation.queue_promoted' as const;

export const ReservationQueuePromotedEventSchema = z.object({
  promotedReservationId: z.number().int(),
  queueEntryId: z.number().int(),
  mediaUnitId: z.number().int(),
  offerId: z.number().int().nullable(),
  /** Copied from the promoted queue entry's own snapshot (CU-86cb7qgc3) — never re-derived from the offer. */
  clientId: z.number().int().nullable(),
  brand: z.string().nullable(),
  holderUserId: z.number().int(),
  dateFrom: z.string(), // ISO date, e.g. '2026-09-01'
  dateTo: z.string(), // ISO date
  subtype: QueueEntrySubtypeSchema,
  promotedFrom: z.enum(['cancellation', 'ttl_expiry', 'manual_admin', 'scheduled']),
});
export type ReservationQueuePromotedEvent = z.infer<typeof ReservationQueuePromotedEventSchema>;
